/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import SearchHostContext from '../../utils/SearchHostContext';
import SearchUtils from '../../utils/SearchUtils';
import { authcompletionRequest } from './authcompletion';
import { completionRequest } from './completion';
import { leoCompletionRequest } from './leoCompletions';
import { leoOnboarding } from './leoOnboardingSuggestions';

const mergeAutoCompletions = (acData: any) => {
	try {
		const [leoResponse = [], ussResponse = {}] = acData;
		const leoAutoPreferOverUSS = SearchUtils.isLeoFloodgateFeatureInTest(
			'ccs.ui.leo.ac.preferLeo',
			SearchHostContext.getContext(),
		);

		const mergedResponse: Record<string, any> = ussResponse || {};
		if (!mergedResponse.suggested_completions) {
			mergedResponse.suggested_completions = [];
		}

		mergedResponse.suggested_completions = mergedResponse.suggested_completions.map((completion) => ({
			...completion,
			source: 'AutoComplete',
		}));

		if (leoAutoPreferOverUSS) {
			mergedResponse.suggested_completions = [...leoResponse, ...mergedResponse.suggested_completions];
		} else {
			mergedResponse.suggested_completions = [...mergedResponse.suggested_completions, ...leoResponse];
		}
		return mergedResponse;
	} catch (e) {
		return {};
	}
};

const performAutoCompletionsRequest = async ({
	searchQuery,
	searchConfig,
}: {
	searchQuery: string;
	searchConfig: any;
}) => {
	const authenticated = SearchHostContext.isAuthenticated();
	if (!authenticated && !searchConfig?.ui?.autoComplete?.enableUnauthenticated) {
		return {};
	}

	const context = SearchHostContext.getContext();
	const useCompletions = searchConfig?.ui?.autoComplete?.useCompletions;

	const leoExperienceEnabled = SearchUtils.isLeoFloodgateFeatureInTest('ccs.ui.leo.experience', context);
	const leoAutoSuggestEnabled = SearchUtils.isLeoFloodgateFeatureInTest('ccs.ui.leo.ac.leo', context);
	const ussAutoCompleteWithLeoEnabled = SearchUtils.isLeoFloodgateFeatureInTest('ccs.ui.leo.ac.uss', context);

	try {
		const ussPromise = useCompletions
			? completionRequest({ searchQuery, searchConfig, searchHostContext: context })
			: authcompletionRequest({ searchQuery, searchConfig, searchHostContext: context });

		const acDataPromises = [];

		const leoPromise =
			leoExperienceEnabled && leoAutoSuggestEnabled
				? leoCompletionRequest({ searchQuery, searchConfig, searchHostContext: context })
				: Promise.resolve([]);

		acDataPromises.push(leoPromise);

		if (leoExperienceEnabled) {
			const ussPromiseConditional = ussAutoCompleteWithLeoEnabled ? ussPromise : Promise.resolve({});
			acDataPromises.push(ussPromiseConditional);
		} else {
			acDataPromises.push(ussPromise);
		}

		const [leoResponse = [], ussResponse = {}] = await Promise.all(acDataPromises);

		return mergeAutoCompletions([leoResponse, ussResponse]);
	} catch (e) {
		return {};
	}
};

export { performAutoCompletionsRequest, leoOnboarding as performLeoOnboardingRequest };
